import CloseIcon from "@mui/icons-material/Close";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import {
    Box,
    CircularProgress,
    ClickAwayListener,
    Drawer,
    IconButton,
    List,
    ListItem,
    Paper,
    Popper,
    Stack,
    SxProps,
    Tooltip,
    Typography,
    useTheme
} from "@mui/material";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { Dispatch, SetStateAction, useState } from "react";
import {
    baseAtom,
    getViewMode,
    setDiscoveryFocusMode,
    setDiscoveryViewMode
} from "../../stores/jotai/base";
import { interviewsAtom } from "../../stores/jotai/interviews/interviews";
import DiscoveryControls from "./discovery-controls";
import { RenderModals } from "./discovery-navigation";
import InterviewListTabs from "./interview/InterviewListTabs";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import MaterialModal from "../app/material-modal";
import { selectAtom } from "jotai/utils";
import { useKeycloak } from "@react-keycloak/web";
import { exportInterviewAtom, setExportInterviewProgress } from "../../stores/jotai/generation-progress/export/exportInterviewProcess";
import IwButton from "../app/IwButton";
import { useExportInterviewForTeam } from "../../tanstackQuery/queries/interviews/useExportInterview";
import { renderErrorToast } from "../../../lib/ErrorToastMessage";
import { AI_GENERATION_PROGRESS } from "../../constants";

interface InterviewListDrawerProps {
  open: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  interviews: any[];
  interviewSearch: any;
  interviewList: any;
  setReopenMenu: Dispatch<SetStateAction<boolean>>;
  anchorEl:any;
}

interface InterviewListBoxProps {
  openInterviewListDrawer: boolean;
  setReopenMenu: Dispatch<SetStateAction<boolean>>;
  interviews: any[];
  interviewSearch: any;
  interviewList: any;
  setIsOpen?:Function;
}

const TEXT_STYLE: SxProps = {
  fontSize: "15px",
  fontFamily: "Inter, sans-serif",
  fontWeight: 500,
  color: "rgba(0, 0, 0, 0.8)",
};

const teamAtom =  selectAtom(baseAtom, (base) => base.team)

const ExportInterviewModal = ({
  showExportInterviewModal,
  setShowExportInterviewModal,
  isEnableTeamInterviewDownload,
  isTeamInterviewDownloadLoading,
  handleTeamExportInterviews,
}) => {
  const handleExportInterviewModalClose = ()=>{
    setShowExportInterviewModal(false)
  }
  return (
    <MaterialModal
      title="Export All Interviews"
      open={showExportInterviewModal}
      onClose={handleExportInterviewModalClose}
      handleDialogClose={handleExportInterviewModalClose}
      styleOverrides={{
        container: {
          "& .MuiDialog-paper": {
            width: "100%",
            minWidth: "300px",
          },
        },
      }}
    >
      <Stack>
        <Typography sx={{ ...TEXT_STYLE }}>
          You're about to download all interview data, including:
        </Typography>
        <List sx={{ listStyleType: "disc", ml: 4 }}>
          <ListItem sx={{ display: "list-item", p: 0.4 }}>
            Interview details{" "}
          </ListItem>
          <ListItem sx={{ display: "list-item", p: 0.4 }}>Notes </ListItem>
          <ListItem sx={{ display: "list-item", p: 0.4 }}>
            Basic transcriptions{" "}
          </ListItem>
          <ListItem sx={{ display: "list-item", p: 0.4 }}>Insights </ListItem>
        </List>
        <Typography sx={{ ...TEXT_STYLE }}>
          Would you like to proceed?
        </Typography>
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "flex-end",
            paddingTop: "8px",
          }}
          spacing={1}
        >
          <IwButton
            variant="outlined"
            onClick={handleExportInterviewModalClose}
          >
            Cancel
          </IwButton>
          <IwButton
            variant="contained"
            disabled={
              isEnableTeamInterviewDownload || isTeamInterviewDownloadLoading
            }
            onClick={handleTeamExportInterviews}
          >
            Download
          </IwButton>
        </Stack>
      </Stack>
    </MaterialModal>
  );
};



export function InterviewListBox(props: InterviewListBoxProps) {
  const [base, setBase] = useAtom(baseAtom);
  const [allInterview] = useAtom(interviewsAtom);
  const [team] = useAtom(teamAtom);
  const [isEnableTeamInterviewDownload, setIsEnableTeamInterviewDownload] = useState(false);
  const [showExportInterviewModal, setShowExportInterviewModal] = useState(false);
  const { keycloak } = useKeycloak();
  const theme = useTheme();

  const setExportInterviewState = useSetAtom(exportInterviewAtom);
  const exportInterviewState = useAtomValue(exportInterviewAtom);

  const publishedInterviewLength = allInterview?.filter(
    (interview) => interview?.status === "published"
  )?.length;


  const {
    refetch,
    isLoading: isTeamInterviewDownloadLoading,
  } = useExportInterviewForTeam(
    { accessToken: keycloak?.token },
    { groupID: team.groupID, sessionID: base.discoverySession?.sessionID },
    { enabled: isEnableTeamInterviewDownload }
  );

  const handleTeamExportInterviews = async () => {
    if (isTeamInterviewDownloadLoading) return;
    setIsEnableTeamInterviewDownload(true);

    try {
      const { data, isSuccess } = await refetch();
      if (isSuccess) {
        if (data?.generationStarted) {
          setExportInterviewProgress(setExportInterviewState, team.groupID, AI_GENERATION_PROGRESS.IN_PROGRESS);
        } else {
          setExportInterviewProgress(setExportInterviewState, team.groupID, AI_GENERATION_PROGRESS.NOT_STARTED);
        }
      } else {
        renderErrorToast("Export failed: try again later");
      }
    } catch {
      renderErrorToast("Export failed: try again later");
    } finally {
      setIsEnableTeamInterviewDownload(false);
      setShowExportInterviewModal(false);
    }
  };

  return (
    <>
      <ExportInterviewModal
        showExportInterviewModal={showExportInterviewModal}
        isEnableTeamInterviewDownload={isEnableTeamInterviewDownload}
        isTeamInterviewDownloadLoading={isTeamInterviewDownloadLoading}
        handleTeamExportInterviews={handleTeamExportInterviews}
        setShowExportInterviewModal={setShowExportInterviewModal}
      />

      {<RenderModals setIsOpen={props?.setIsOpen} />}
      <div
        style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}
      >
        {!props.openInterviewListDrawer ? (
          <Box sx={{ paddingRight: "5px" }}>
            <Tooltip title={"Hide Interview List"}>
              <IconButton
                id="joy-discovery-focus-mode"
                onClick={() => {
                  setDiscoveryFocusMode(setBase, !base.discoveryFocusMode);
                  props?.setReopenMenu(false);
                }}
                sx={{ height: "38px" }}
              >
                <MenuOpenIcon />
              </IconButton>
            </Tooltip>
          </Box>
        ) : null}
        {props.interviewSearch}
        <Tooltip title={"Export All Interviews"}>
          <IconButton
            sx={{
              border: `1px solid ${theme.custom.borderGrey}`,
              borderRadius: "4px",
              py: "8px",
              px: "8px",
              ml: "5px",
              ":hover": {
                color: theme.custom.mainButtonHighlight,
                border: "1px solid #1976D2",
              },
            }}
            onClick={() => setShowExportInterviewModal(true)}
            disabled={
              isEnableTeamInterviewDownload ||
              isTeamInterviewDownloadLoading ||
              exportInterviewState?.[team?.groupID]
                ?.exportInterviewInProgress ===
                AI_GENERATION_PROGRESS?.IN_PROGRESS ||
              publishedInterviewLength + props?.draftInterviewLength === 0
            }
          >
            {isEnableTeamInterviewDownload ||
            isTeamInterviewDownloadLoading ||
            exportInterviewState?.[team?.groupID]?.exportInterviewInProgress ===
              AI_GENERATION_PROGRESS?.IN_PROGRESS ? (
              <CircularProgress size={16} sx={{ marginLeft: "2px" }} />
            ) : (
              <FileDownloadIcon fontSize="medium" />
            )}
          </IconButton>
        </Tooltip>
      </div>

      <InterviewListTabs
        value={getViewMode(base, props?.interviews)}
        onChange={(e, t) =>
          setDiscoveryViewMode(setBase, t, true, allInterview)
        }
        draftInterviewLength={allInterview?.length - publishedInterviewLength}
        publishedInterviewLength={publishedInterviewLength}
      />

      <Box
        sx={{
          height: "calc(100vh - 330px)",
        }}
      >
        <Paper
          // className="top-level-discovery"
          id="joy-discovery-interview-table"
          sx={{
            width: "100%",
            overflow: "hidden",
            height: "100%",
            borderRadius: "0px",
          }}
        >
          <Box sx={{ display: "flex", height: "100%", marginTop: "10px", }}>
            {props?.interviewList}
          </Box>
        </Paper>
      </Box>
    </>
  );
}

export function InterviewListDrawer(props: InterviewListDrawerProps) {
  const { open, setIsOpen, anchorEl } = props;
  const theme = useTheme();

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      disablePortal
      style={{ 
        zIndex: 5,
      }}
    >
      <ClickAwayListener onClickAway={() => setIsOpen(false)}>
        <Paper
          sx={{
            height: "100%",
            minWidth: "600px",
            maxWidth: "600px",
            margin: "12px 0 0",
            padding: "6px",
            backgroundColor: "#f4f4f4",
            border: `1px solid ${theme.custom.borderGrey}`,
          }}
        >
          <Typography fontSize={20} fontWeight={600}>
            Interview list
          </Typography>
          <Box mt={1.5}>
            <InterviewListBox
              setReopenMenu={props.setReopenMenu}
              interviewSearch={props.interviewSearch}
              interviews={props.interviews}
              interviewList={props.interviewList}
              openInterviewListDrawer={open}
              setIsOpen={setIsOpen}
            />
          </Box>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
}